import { ByzzerTable } from '@/components/ByzzerTable';
import { DodFactFilterType, DodFilterType, DodProductFilterTypes, RowColConfig } from '@/components/DodConfigEditor/types';
import { useApp, useUser } from '@/contexts/UserContext';
import {
    ByzzerButton,
    ByzzerChangeEventHandler,
    ByzzerCheckableChangeEvent,
    ByzzerCheckbox,
    ByzzerTipIcon,
} from '@byzzer/ui-components';
import { ColDef, GridOptions, CellClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DodFactValuePickerTable.scss';
import { DodFactSet, DodFilters } from '@/types/DodRun';
import { ByzzerLink } from '@/components/form';
import { cloneDeep, debounce, intersection } from "lodash";
import useStateRef from "react-usestateref";
import { TippyProps } from "@tippyjs/react";
import { useCategoryService } from "@/services/category.service";
import {
    alertSelectionLimitExceed,
    filterValuesToStrings,
    isBrandFactStacked,
    isCategoryFactStacked,
    isCdiBdiFact,
    isProductSelectionsFactStacked,
    isSubCategoryFactStacked,
    isWeeksSellingFact,
    isWorkInProgressFactsDisabled,
} from '@/components/DodConfigEditor/common/utils';
import { ByzColDef } from '@/types/TableTypes';
import { getRegexForSearchString } from '@/utils';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { PlatformLink } from '@/types/ApiTypes';

export type DodFactValuePickerTableProps = {
    name?: string;
    className?: string;
    filterType: DodFilterType<DodFactFilterType>;
    actions?: ActionConfig[];
    includeActions?: boolean;
    filters: DodFilters;
    value: DodFactSet[];
    filterText?: string;
    onApply?: ByzzerChangeEventHandler<DodFactSet[]>;
    limit?: number;
    factTriggerRef: any;
    acvArticleLinkData: PlatformLink | null;
};
const baseClassName = 'dod-fact-value-picker-table';

const defaultTippyOptions: TippyProps = {
    appendTo: () => {
        return document.body;
    }
}

const checkForTooltipCondition = (displayName) => {
    if (['% ACV (Max)', '$ ACV Selling (Max)'].includes(displayName)) return true;
    if (
        displayName.includes('% $ Incremental') ||
        displayName.includes('% Units Incremental') ||
        displayName.includes('% $ Lift') ||
        displayName.includes('% Unit Lift')
    )
        return true;
    return false;
};

const tooltipForFact = (fact) => {
    let customFactName = fact;
    if (fact === '% ACV (Max)') {
        customFactName = '% ACV';
    } else if (fact === '$ ACV Selling (Max)') {
        customFactName = '$ ACV Selling';
    } else if (fact.includes('% $ Incremental') || fact.includes('% Units Incremental')) {
        customFactName = 'Incremental';
    } else if (fact.includes('% $ Lift') || fact.includes('% Unit Lift')) {
        customFactName = 'Lift';
    }

    // const factSell = fact === '% ACV (Max)' ? '% ACV' : '$ ACV Selling';
    switch (customFactName) {
        case '% ACV':
            return `At the UPC and week level, ${fact} is the same fact as ${customFactName}. ${fact} differs from ${customFactName} at any hierarchy level that is higher than UPC, because it is the maximum ${customFactName} for the items and weeks combination based on the selections.`;
        case '$ ACV Selling':
            return `At the UPC level, ${fact} is the same fact as ${customFactName}. ${fact} differs from ${customFactName} at any hierarchy level that is higher than UPC, because it is the maximum ${customFactName} out of any item based on the selections.`;
        case 'Incremental':
            return `${fact} is the incremental volume compared to the observed volume. Use this metric to understand what portion of your total sales were incremental. Formula = (Incremental Volume/Observed Volume)*100`;
        case 'Lift':
            return `${fact} is the incremental volume compared to the base volume. Use this metric to understand the % of sales that were gained over the base, or expected, volume. Formula = (Incremental Volume/Base Volume)*100`;
        default:
            break;
    }
};
const factToolTipForTDPAndACV = (dataItem: DodFactSet, acvArticleLinkData: PlatformLink | null) => {    
    // todo: checkCategoryForDepartment to be added
    if ((dataItem.display.toLocaleLowerCase().includes('tdp') || dataItem.display.toLocaleLowerCase().includes('acv'))) {
        return (
            <ByzzerTipIcon
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                tip={
                    <div>
                        Distribution facts are calculated using Category Weighted Distribution for all Alcohol products.
                        Read our{' '}
                        <ByzzerLink
                            theme={'dark'}
                            // @ts-ignore - does 'rel' do anything here? throws type error...
                            rel="noreferrer"
                        >
                            <a
                                target="_blank"
                                href={acvArticleLinkData?.linkUrl ?? '#'}
                            >
                                {acvArticleLinkData?.linkText ?? 'ACV Article'}
                            </a>
                        </ByzzerLink>{' '}
                        to learn more about what this means.
                    </div>
                }
            />
        );
    }
    return null;
};

const factToolTipForCostcoTDPAndACV = (dataItem: DodFactSet) => {
    if ((dataItem.display.toLocaleLowerCase().includes('tdp') || dataItem.display.toLocaleLowerCase().includes('acv') || dataItem.display.toLocaleLowerCase().includes('average number of stores selling'))) {
        return (
            <ByzzerTipIcon
                type={'warning'}
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                tip={
                    <div>
                        Distribution facts will not be reported for the Costco market.
                    </div>
                }
            />
        );
    }
    return null;
};

const factToolTipForLaunchDate = (dataItem: DodFactSet) => {
    if (dataItem.display.toLocaleLowerCase() === 'launch date') {
        return (
            <ByzzerTipIcon
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                tip={<div>
                    Launch date is the first date a product is present in any market. Products that have been selling earlier than the Byzzer dataset will always show the Launch Date as the earliest date in the data.
                </div>}
            />
        );
    }
    return null;
};

const factToolTipForStackedField = (rowColConfigs: RowColConfig[], flag: string) => {
    let tip: string | React.ReactNode = '';
        if (isCategoryFactStacked(rowColConfigs, flag)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Category. Unstack this field if you wish to add Share of Category facts.</div>
        }
        else if (isSubCategoryFactStacked(rowColConfigs, flag)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Subcategory. Unstack this field if you wish to add Share of Subcategory facts.</div>;
        }
        else if (isBrandFactStacked(rowColConfigs, flag)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Brand. Unstack this field if you wish to add Share of Brand facts.</div>
        }
        else if (isProductSelectionsFactStacked(rowColConfigs, flag)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked product(s). Unstack these field(s) if you wish to add Share of Product Selections facts.</div>;
        }
        return tip;
}

const checkboxColumnWidth = 65;
const flagToFactMapping: Record<string, string> = {
    cp: 'fact',
    ya: 'yearAgo',
    cvya: 'changeVsYearAgo',
    pcvya: 'percentChangeVsYearAgo',
    c: 'category',
    cya: 'categoryYearAgo',
    ctya: 'categoryTwoYearAgo',
    ctya3: 'categoryThreeYearAgo',
    cfya: 'categoryFourYearAgo',
    b: 'brand',
    bya: 'brandYearAgo',
    btya: 'brandTwoYearAgo',
    btya3: 'brandThreeYearAgo',
    bfya: 'brandFourYearAgo',
    ps: 'productSelections',
    psya: 'productSelectionsYearAgo',
    pstya: 'productSelectionsTwoYearAgo',
    pstya3: 'productSelectionsThreeYearAgo',
    psfya: 'productSelectionsFourYearAgo',
    tya: 'twoYearAgo',
    tya3: 'threeYearAgo',
    fya: 'fourYearAgo',
    sc: 'subCategory',
    scya: 'subCategoryYearAgo',
    sctya: 'subCategoryTwoYearAgo',
    sctya3: 'subCategoryThreeYearAgo',
    scfya: 'subCategoryFourYearAgo',
    cvtya: 'changeVsTwoYearAgo',
    pcvtya: 'percentChangeVsTwoYearAgo',
    cvtya3: 'changeVsThreeYearAgo',
    pcvtya3: 'percentChangeVsThreeYearAgo',
    cvfya: 'changeVsFourYearAgo',
    pcvfya: 'percentChangeVsFourYearAgo'

}

const flagToAllowedIndexMapping: Record<string, number> = {
    cp: 1,
    ya: 2,
    cvya: 4,
    pcvya: 11,
    c: 5,
    cya: 6,
    b: 7,
    bya: 8,
    ps: 9,
    psya: 10,
    tya: 3,
    tya3: 14,
    fya: 15,
    sc: 12,
    scya: 13,
    cvtya: 16,
    pcvtya: 19,
    cvtya3: 17,
    pcvtya3: 20,
    cvfya: 18,
    pcvfya: 21,
    ctya: 22,
    ctya3: 23,
    cfya: 24,
    btya: 25,
    btya3: 26,
    bfya: 27,
    pstya: 28,
    pstya3: 29,
    psfya: 30,
    sctya: 31,
    sctya3: 32,
    scfya: 33
}

const coreFlags: string[] = [
    'cp',
    'ya',
    'cvya',
    'pcvya',
    'tya',
    'tya3',
    'fya',
    'cvtya',
    'pcvtya',
    'cvtya3',
    'pcvtya3',
    'cvfya',
    'pcvfya'
];
const shareFlags: string[] = [
    'c',
    'cya',
    'ctya',
    'ctya3',
    'cfya',
    'b',
    'bya',
    'btya',
    'btya3',
    'bfya',
    'ps',
    'psya',
    'pstya',
    'pstya3',
    'psfya',
    'sc',
    'scya',
    'sctya',
    'sctya3',
    'scfya'
];

const checkboxGridOptions: GridOptions = {
    rowHeight: 24
}
const defaultColDef = {
    wrapText: true,
    autoHeight: true,
};

type FlagAndIndex = {
    flag: string;
    index: number;
}

type DodFactValuePickerTableColId = 'subCategoryFourYearAgoCheckbox' | 'coreDisplay' | 'shareDisplay' | 'currentPeriodCheckbox' | 'yearAgoCheckbox' | 'changeVsYearAgoCheckbox' | 'percentChangeVsYearAgoCheckbox' | 'categoryCheckbox' | 'categoryYearAgoCheckbox' | 'categoryTwoYearAgoCheckbox' | 'categoryThreeYearAgoCheckbox' | 'categoryFourYearAgoCheckbox' | 'brandCheckbox' | 'brandYearAgoCheckbox' | 'brandTwoYearAgoCheckbox' | 'brandThreeYearAgoCheckbox' | 'brandFourYearAgoCheckbox' | 'productSelectionsCheckbox' | 'productSelectionsYearAgoCheckbox' | 'productSelectionsTwoYearAgoCheckbox' | 'productSelectionsThreeYearAgoCheckbox' | 'productSelectionsFourYearAgoCheckbox' | 'subCategoryCheckbox' | 'subCategoryYearAgoCheckbox' | 'subCategoryTwoYearAgoCheckbox' | 'subCategoryThreeYearAgoCheckbox' | 'subCategorFourYearAgoCheckbox' | 'twoYearAgoCheckbox' | 'threeYearAgoCheckbox' | 'fourYearAgoCheckbox' | 'changeVsTwoYearAgoCheckbox' | 'percentChangeVsTwoYearAgoCheckbox' | 'changeVsThreeYearAgoCheckbox' | 'percentChangeVsThreeYearAgoCheckbox' | 'changeVsFourYearAgoCheckbox' | 'percentChangeVsFourYearAgoCheckbox';

// In dod, weeks selling and cdi/bdi facts are in work in progress. These are its facts ids.
// Fact ids remain the same across environments, hence we are using the ids to disable summed product,
// summed market, subtotal and hide category features in dod for these fact selections
// export const weeksSellingFactIds: number[] = [1031];
// export const cdiBdiFactIds: number[] = [1021, 1067, 1068, 1069, 1071, 1072, 1073, 1074, 1075];
const inProgressFactsTooltipLink = 'https://learn.byzzer.ai/en/articles/10542218-number-of-weeks-selling-fact-restrictions';

const DodFactValuePickerTable = ({
    filterType,
    actions,
    includeActions,
    className,
    name,
    onApply,
    filters,
    filterText = '',
    value,
    limit = Infinity,
    factTriggerRef,
    acvArticleLinkData,
    ...props
}: DodFactValuePickerTableProps) => {
    const gridRef = useRef<AgGridReact>(null);
    const { factConfig, categories: companyCategories, features } = useApp();
    const { getCategoriesForDepartments, getAllCategoriesByDepartmentsAndSuperCategories } = useCategoryService();
    const [selections, setSelections] = useState<Record<string, boolean>>({})
    const [currentFacts, setCurrentFacts, currentFactsRef] = useStateRef<any[]>([]);
    const [matchingFacts, setMatchingFacts, matchingFactsRef] = useStateRef<DodFactSet[]>([]);
    const lastFlagAndIndex = useRef<FlagAndIndex | null>(null);
    const shiftActive = useRef<boolean>(false);
    // Snapshot of selections when anchor (first non-shift click) is set
    const shiftBaselineRef = useRef<Record<string, boolean> | null>(null);
    // Track keys that were applied by the most recent shift operation
    const lastAppliedKeysRef = useRef<Set<string>>(new Set());
    // Batched redraw system
    const pendingRowsToRedraw = useRef<Set<any>>(new Set());
    const hasPendingFullRedraw = useRef<boolean>(false);
    const isUPCSelected = Boolean(filters?.upcs.values.length || filters?.upcs.summedSelections.length);
    const isBrandSelected = Boolean(filters?.brands.values.length || filters?.brands.summedSelections.length);
    const isSubCategorySelected = Boolean(filters?.subcategories.values.length || filters?.subcategories.summedSelections.length)
    const { runConfig, rowColConfigs } = useDodWizard();

    useEffect(() => {
        const onKeyUp = (ev: KeyboardEvent) => {
            if (ev.key === 'Shift') {
                // Clear shift mode when Shift is released so next single click behaves normally
                shiftActive.current = false;
                // keep anchor, but drop baseline so subsequent non-shift clicks will snapshot again
                shiftBaselineRef.current = null;
                // clear tracking of last applied keys
                lastAppliedKeysRef.current = new Set();
            }
        };
        window.addEventListener('keyup', onKeyUp);
        return () => window.removeEventListener('keyup', onKeyUp);
    }, []);

    const extendedDataYears = useMemo(() => {
        return features?.extendedDataYears === 5;
    }, [features]);

    // disable a fact if is a upc level fact and requires upc in currect user selection
    const isUpcDisabledFact = (fact: DodFactSet) => {
        return fact?.isUPCrequired && !isUPCSelected;
    };

    // to check if summed product is present in user selections
    const isSummedProductSelected = () => {
        let isSummedProductSelected: boolean = false;
        const productFilters = Object.keys(filters).filter(
            (filter) => filter !== 'markets' && filter !== 'timePeriods'
        );
        for (const filterType of productFilters) {
            if (
                [
                    DodProductFilterTypes.CHARACTERISTICS,
                    DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                    DodProductFilterTypes.PPGS,
                ].includes(filterType as DodProductFilterTypes)
            ) {
                const filterKeys = Object.keys(filters[filterType]);
                for (const key of filterKeys) {
                    if (filters[filterType][key].summedSelections.length) {
                        isSummedProductSelected = true;
                    }
                }

            } else {
                if (filters[filterType].summedSelections.length) {
                    isSummedProductSelected = true;
                }
            }
        }
        return isSummedProductSelected;
    };

    const isFactDisabled = (dataItem: DodFactSet) => {
        return (
            isUpcDisabledFact(dataItem) || isWorkInProgressFactsDisabled({ fact: dataItem, runConfig, rowColConfigs })
        );
    };

    const getDisabledTip = (fact: DodFactSet, extendedDataYears?: boolean) => {
        let tip: string | React.ReactNode = '';
        if (extendedDataYears) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You license the standard 3 years of data. If you wish to upgrade to 5 years of data, please contact your account rep.</div>
        }
        else if (isCdiBdiFact(fact)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>CDI/BDI facts are not compatible with all selection types yet. Please review this <b><u><a className={`${baseClassName}__disabled-tip`} href={inProgressFactsTooltipLink} target="_blank" rel="noreferrer">article</a></u></b> to understand which of your selections are not compatible.</div>
        }
        else if (isWeeksSellingFact(fact)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>Number of Weeks Selling facts are not compatible with all selection types yet. Please review this <b><u><a className={`${baseClassName}__disabled-tip`} href={inProgressFactsTooltipLink} target="_blank" rel="noreferrer">article</a></u></b> to understand which of your selections are not compatible.</div>
        }
        return tip;
    };

    const { categories: userCategories, subscription } = useUser();
    
    const isFreeUser = subscription?.metadata?.isFree ?? false;

    const includesAlcoholCategories = useMemo<boolean>(() => {

        const alcoholCategories = getCategoriesForDepartments(['ALCOHOL']);
        let categories = filters.categories.values as string[];

        if (
            filters.categories.values === 'all' ||
            filters.categories.summedSelections.some((ss) => ss.values === 'all')
        ) {
            categories = getAllCategoriesByDepartmentsAndSuperCategories(filters.departments, filters.superCategories);
        } else {
            categories = filterValuesToStrings(filters.categories, userCategories);
        }
        return Boolean(intersection(alcoholCategories, categories).length);
    }, [filters]);

    const includesCostcoMarket = useMemo<boolean>(() => {
        const isCostcoMarket = item => item.masterCompany === 'Costco';

        const hasCostcoInMarkets = filters.markets.values.some(isCostcoMarket);
        const hasCostcoInSummedSelections = filters.markets.summedSelections.some(
            (selection) => selection.values.some(isCostcoMarket)
        );

        return hasCostcoInMarkets || hasCostcoInSummedSelections;
    }, [filters]);


    const coreFactsColumnDefs: ByzColDef<DodFactSet, DodFactValuePickerTableColId>[] = useMemo(() => [{
        headerName: 'Fact Name',
        colId: 'coreDisplay',
        field: 'display',
        minWidth: 250,
        flex: 1,
        pinned: 'left',
        lockPinned: true,
        sortable: true,
        filter: false,
        headerClass: `${baseClassName}__fact-name-header`,
        cellClass: `${baseClassName}__fact-name-cell`,
        cellRenderer: ({ data }) => (

            <div className={classNames(`${baseClassName}__fact-name`, {
                [`${baseClassName}__fact-name--disabled`]: isFactDisabled(data),
            })}>
                <h1>{data?.display}</h1>
                <div className={`${baseClassName}__info-icons`}>
                    {includesAlcoholCategories && factToolTipForTDPAndACV(data, acvArticleLinkData)}
                    {includesCostcoMarket && factToolTipForCostcoTDPAndACV(data)}
                    {factToolTipForLaunchDate(data)}
                </div>
            </div>
        ),
        tooltipValueGetter: ({ data }) => {
            return checkForTooltipCondition(data?.display) ? tooltipForFact(data?.display) : `${data?.display}`;
        },
        disableHide: true
    }, {
        colId: 'currentPeriodCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Current Period',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cp'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data?.id}:${flag}`;
            if (!data?.allowedFacts?.includes(1)) return null;
            return (
                // TODO: To remove the commented ByzzerMessagePopUp components post BYZ-15610 testing
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={Boolean(value) && Boolean(context) ? context?.[value] : undefined}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'yearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(2)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'changeVsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cvya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(11)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'percentChangeVsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pcvya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(4)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'twoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'tya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(3)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cvtya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(16)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pcvtya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(19)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'threeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'tya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(14)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'cvtya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(17)) return null;
            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'pcvtya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(20)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'fourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 Year-Ago',
        hide: !extendedDataYears,
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'fya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(15)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'cvfya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(18)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'pcvfya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(21)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }
    ], []);
    const shareFactsColumnDefs: ByzColDef<DodFactSet, DodFactValuePickerTableColId>[] = useMemo(() => [{
        headerName: 'Fact Name',
        colId: 'shareDisplay',
        field: 'display',
        minWidth: 250,
        flex: 1,
        pinned: 'left',
        sortable: true,
        lockPinned: true,
        filter: true,
        headerClass: `${baseClassName}__fact-name-header`,
        cellClass: `${baseClassName}__fact-name-cell`,
        cellRendererParams: {
            flag: ''
        },
        disableHide: true,
        cellRenderer: ({ data }) => (
            <div className={classNames(`${baseClassName}__fact-name`, {
                [`${baseClassName}__fact-name--disabled`]: isFactDisabled(data),
            })}>
                <h1>{data?.display}</h1>
                <div className={`${baseClassName}__info-icons`}>
                    {includesAlcoholCategories && factToolTipForTDPAndACV(data, acvArticleLinkData)}
                    {includesCostcoMarket && factToolTipForCostcoTDPAndACV(data)}
                </div>
            </div>
        ),
        tooltipValueGetter: ({ data }) => {
            return checkForTooltipCondition(data?.display) ? tooltipForFact(data?.display) : `${data?.display}`;
        },
    }, {
        colId: 'categoryCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Category',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'c'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(5)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'categoryYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Category Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(6)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categoryTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Category 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ctya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(22)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categoryThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Category 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(23)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'categoryFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Category 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(24)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'subCategoryCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Subcategory',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sc'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(12)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Subcategory Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(13)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryTwoYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Subcategory 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sctya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(31)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryThreeYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Subcategory 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(32)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'subCategoryFourYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Subcategory 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(33)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'brandCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Brand',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'b'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(7)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isUPCSelected && !isBrandSelected || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'brandYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Brand Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(8)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isUPCSelected && !isBrandSelected || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'brandTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Brand 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'btya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(25)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={(!isUPCSelected && !isBrandSelected) || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'brandThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Brand 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'btya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(26)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        (!isUPCSelected && !isBrandSelected) ||
                        !extendedDataYears ||
                        isBrandFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }, {
        colId: 'brandFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Brand 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(27)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        (!isUPCSelected && !isBrandSelected) ||
                        !extendedDataYears ||
                        isBrandFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'productSelectionsCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Product Selections',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ps'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(9)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'productSelectionsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Product Selections Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(10)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Product Selections 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pstya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(28)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Product Selections 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pstya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(29)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'productSelectionsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Product Selections 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(30)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        }
    }], []);

    useEffect(() => {
        setSelections(() => {


            return value.reduce((selections, factSet) => {

                const {
                    id,
                    fact: currentPeriod,
                    yearAgo,
                    changeVsYearAgo,
                    percentChangeVsYearAgo,
                    twoYearAgo,
                    threeYearAgo,
                    fourYearAgo,
                    category,
                    categoryYearAgo,
                    categoryTwoYearAgo,
                    categoryThreeYearAgo,
                    categoryFourYearAgo,
                    brand,
                    brandYearAgo,
                    brandTwoYearAgo,
                    brandThreeYearAgo,
                    brandFourYearAgo,
                    productSelections,
                    productSelectionsYearAgo,
                    productSelectionsTwoYearAgo,
                    productSelectionsThreeYearAgo,
                    productSelectionsFourYearAgo,
                    subCategory,
                    subCategoryYearAgo,
                    subCategoryTwoYearAgo,
                    subCategoryThreeYearAgo,
                    subCategoryFourYearAgo,
                    changeVsTwoYearAgo,
                    changeVsThreeYearAgo,
                    changeVsFourYearAgo,
                    percentChangeVsTwoYearAgo,
                    percentChangeVsThreeYearAgo,
                    percentChangeVsFourYearAgo
                } = factSet;
                selections[`${id}:cp`] = Boolean(currentPeriod);
                selections[`${id}:ya`] = Boolean(yearAgo);
                selections[`${id}:cvya`] = Boolean(changeVsYearAgo);
                selections[`${id}:pcvya`] = Boolean(percentChangeVsYearAgo);
                selections[`${id}:tya`] = Boolean(twoYearAgo);
                selections[`${id}:cvtya`] = Boolean(changeVsTwoYearAgo);
                selections[`${id}:pcvtya`] = Boolean(percentChangeVsTwoYearAgo);
                selections[`${id}:tya3`] = Boolean(threeYearAgo);
                selections[`${id}:cvtya3`] = Boolean(changeVsThreeYearAgo);
                selections[`${id}:pcvtya3`] = Boolean(percentChangeVsThreeYearAgo);
                selections[`${id}:fya`] = Boolean(fourYearAgo);
                selections[`${id}:cvfya`] = Boolean(changeVsFourYearAgo);
                selections[`${id}:pcvfya`] = Boolean(percentChangeVsFourYearAgo);
                selections[`${id}:c`] = Boolean(category);
                selections[`${id}:cya`] = Boolean(categoryYearAgo);
                selections[`${id}:ctya`] = Boolean(categoryTwoYearAgo);
                selections[`${id}:ctya3`] = Boolean(categoryThreeYearAgo);
                selections[`${id}:cfya`] = Boolean(categoryFourYearAgo);
                selections[`${id}:b`] = Boolean(brand);
                selections[`${id}:bya`] = Boolean(brandYearAgo);
                selections[`${id}:btya`] = Boolean(brandTwoYearAgo);
                selections[`${id}:btya3`] = Boolean(brandThreeYearAgo);
                selections[`${id}:bfya`] = Boolean(brandFourYearAgo);
                selections[`${id}:ps`] = Boolean(productSelections);
                selections[`${id}:psya`] = Boolean(productSelectionsYearAgo);
                selections[`${id}:pstya`] = Boolean(productSelectionsTwoYearAgo);
                selections[`${id}:pstya3`] = Boolean(productSelectionsThreeYearAgo);
                selections[`${id}:psfya`] = Boolean(productSelectionsFourYearAgo);
                selections[`${id}:sc`] = Boolean(subCategory);
                selections[`${id}:scya`] = Boolean(subCategoryYearAgo);
                selections[`${id}:sctya`] = Boolean(subCategoryTwoYearAgo);
                selections[`${id}:sctya3`] = Boolean(subCategoryThreeYearAgo);
                selections[`${id}:scfya`] = Boolean(subCategoryFourYearAgo);
                return selections;
            }, {} as Record<string, boolean>)
        })
        queueFullRedraw();
    }, [value]);

    useEffect(() => {
        const facts = factConfig?.[filterType.data.type]
            ?.find((val) => val.value === filterType.type)
            ?.factLists ?? [];
        setCurrentFacts(facts);
        setMatchingFacts(facts);
    }, [filterType, factConfig]);

    useEffect(() => {
        filterFacts(filterText);
    }, [filterText]);

    const filterFacts = useCallback(debounce(async (filterText: string) => {
        if (filterText) {
            const matcher = getRegexForSearchString(filterText);
            setMatchingFacts(currentFactsRef.current.filter(({ display }) => matcher.test(display)));
        } else {
            setMatchingFacts(currentFactsRef.current);
        }
    }, 500), []);

    function handleCheckboxClick(e: MouseEvent, index: number, flag: string): void {
        // This function is responsible for handle shift select
        if (!(e.target as HTMLElement).matches('input[type="checkbox"]')) return;

        // If shift was previously active but is no longer pressed, reset shift state
        if (shiftActive.current && !e.shiftKey) {
            shiftActive.current = false;
        }

        // Block shift-to-select for free users
        if (e.shiftKey && isFreeUser) {
            // Idea - We could show a tooltip or message about upgrading to access this feature
            return;
        }

        if (e.shiftKey && lastFlagAndIndex.current) {
            // Important: preventDefault to stop native checkbox behavior
            e.preventDefault();
            
            const startRow = Math.min(lastFlagAndIndex.current.index, index);
            const endRow = Math.max(lastFlagAndIndex.current.index, index);

            const allColDefs = filterType.data.type === 'core' ? coreFactsColumnDefs : shareFactsColumnDefs;
            const allFlags = allColDefs
                .map(col => col.cellRendererParams?.flag)
                .filter(Boolean) as string[];

            const startColIndex = allFlags.indexOf(lastFlagAndIndex.current.flag);
            const endColIndex = allFlags.indexOf(flag);

            if (startColIndex === -1 || endColIndex === -1) {
                return;
            }

            const startCol = Math.min(startColIndex, endColIndex);
            const endCol = Math.max(startColIndex, endColIndex);

            // Use baseline snapshot (if available) so we don't destructively remove unrelated selections.
            const baseline = shiftBaselineRef.current ? { ...shiftBaselineRef.current } : cloneDeep(selections);

            // Anchor state is read from the baseline so repeated shift-reshapes are deterministic
            const anchorKey = `${matchingFactsRef.current[lastFlagAndIndex.current.index].id}:${lastFlagAndIndex.current.flag}`;
            const anchorState = baseline[anchorKey];
            const isChecking = Boolean(anchorState);

            // Start from baseline and revert any previous shift-applied keys that are now outside the new rectangle
            const newSelections = { ...baseline };

            // Get keys that were applied by the previous shift operation
            const prevAppliedKeys = lastAppliedKeysRef.current;

            // Build set of keys that will be applied by the new rectangle
            const newAppliedKeys = new Set<string>();
            const rowsToRedrawSet = new Set<any>();

            for (let i = startRow; i <= endRow; i++) {
                const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(i);
                if (!rowNode?.data) continue;
                rowsToRedrawSet.add(rowNode);

                const fact = rowNode.data as DodFactSet;
                for (let j = startCol; j <= endCol; j++) {
                    const currentFlag = allFlags[j];
                    const valueKey = `${fact.id}:${currentFlag}`;
                    
                    const isAllowed = fact.allowedFacts?.includes(flagToAllowedIndexMapping[currentFlag]);

                    // Get the column definition for this flag
                    const colDef = allColDefs.find(col => col.cellRendererParams?.flag === currentFlag);
                    let isDisabled = false;

                    if (colDef?.cellRenderer && typeof colDef.cellRenderer === 'function') {
                        // Simulate the exact same parameters that the cell renderer receives.
                        const rendererParams = {
                            data: fact,
                            flag: currentFlag,
                            rowIndex: i,
                            context: selections
                        };

                        // Call the cell renderer to get the actual component
                        // @ts-expect-error - rendererParams is a partial match for the actual params.  It works for our purposes, disabling logic.
                        const cellComponent = colDef.cellRenderer(rendererParams);

                        // Extract the disabled state from the ByzzerCheckbox props
                        if (cellComponent && typeof cellComponent === 'object' && 'props' in cellComponent) {
                            isDisabled = Boolean(cellComponent.props?.disabled);
                        }
                    }

                    if (isAllowed && !isDisabled) {
                        newAppliedKeys.add(valueKey);
                    }
                }
            }

            // Revert any previous shift-applied keys that are not in the new rectangle
            // AND add their rows to the redraw set so the UI updates to show deselection
            for (const prevKey of prevAppliedKeys) {
                if (!newAppliedKeys.has(prevKey)) {
                    // Find which row this key belongs to and mark it for redraw
                    const [factId] = prevKey.split(':');
                    const factIndex = matchingFactsRef.current.findIndex(fact => fact.id === Number(factId));
                    if (factIndex !== -1) {
                        const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(factIndex);
                        if (rowNode?.data) {
                            rowsToRedrawSet.add(rowNode);
                        }
                    }

                    // Revert to baseline value (might be undefined, which means delete)
                    if (baseline.hasOwnProperty(prevKey)) {
                        newSelections[prevKey] = baseline[prevKey];
                    } else {
                        delete newSelections[prevKey];
                    }
                }
            }

            // Apply the new rectangle keys
            for (const key of newAppliedKeys) {
                newSelections[key] = isChecking;
            }

            // Update tracking of which keys were applied by this shift operation
            lastAppliedKeysRef.current = newAppliedKeys;

            // Apply the new selection state (functional update to avoid stale closures)
            setSelections(() => newSelections);
            // Queue affected rows for redraw
            const rowNodesToRedraw = Array.from(rowsToRedrawSet);
            if (rowNodesToRedraw.length) {
                queueRowRedraw(rowNodesToRedraw);
            }

            // Keep shift mode active for continuous shift-selections
            shiftActive.current = true;
        } else {
            // For a normal click, update the anchor point
            lastFlagAndIndex.current = { index, flag };
            shiftActive.current = false;
            // snapshot current selections as baseline for subsequent shift operations
            shiftBaselineRef.current = cloneDeep(selections);
            // clear tracking when setting new anchor
            lastAppliedKeysRef.current = new Set();
            // Let the normal checkbox change handler run
        }
    }

    function handleFactChange({ value, checked }: ByzzerCheckableChangeEvent<string>) {
        if (shiftActive.current) {
            shiftActive.current = false;
            return;
        }
        setSelections(selections => {
            const isSelectionLimitExceed = Object.keys(selections).filter((fact) => selections[fact] === true).length >= limit
            if (checked && isSelectionLimitExceed) {
                alertSelectionLimitExceed('facts', limit)
            }
            return ({
                ...selections,
                ...(!isSelectionLimitExceed || !checked) && { [value]: checked }
            })
        });
        // Queue redraw for the specific row that changed
        const [factId] = value.split(':');
        const factIndex = matchingFactsRef.current.findIndex(fact => fact.id === Number(factId));
        if (factIndex !== -1) {
            const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(factIndex);
            if (rowNode?.data) {
                queueRowRedraw([rowNode]);
            } else {
                queueFullRedraw();
            }
        } else {
            queueFullRedraw();
        }
    }

    function handleClearClick() {
        lastFlagAndIndex.current = null;
        shiftActive.current = false;
        shiftBaselineRef.current = null;
        lastAppliedKeysRef.current = new Set();
        // only remove the
        const flags = filterType.data.type === 'core' ? coreFlags : shareFlags;
        const factIds: number[] = currentFactsRef.current.map(fact => fact.id);

        setSelections(selections => {
            const newSelections = { ...selections };

            for (let flag of flags) {
                for (let id of factIds) {
                    delete newSelections[`${id}:${flag}`];
                }
            }

            return newSelections;
        });
        
        // Clear affects all rows, so queue full redraw
        queueFullRedraw();
    }

    const flushRedrawQueue = useCallback(debounce(() => {
        try {
            if (hasPendingFullRedraw.current) {
                // Full redraw takes precedence
                gridRef.current?.api.redrawRows();
                hasPendingFullRedraw.current = false;
                pendingRowsToRedraw.current.clear();
            } else if (pendingRowsToRedraw.current.size > 0) {
                // Partial redraw of specific rows
                const rowNodes = Array.from(pendingRowsToRedraw.current);
                gridRef.current?.api.redrawRows({ rowNodes });
                pendingRowsToRedraw.current.clear();
            }
        } catch {
            // Fallback to full redraw on error
            gridRef.current?.api.redrawRows();
            hasPendingFullRedraw.current = false;
            pendingRowsToRedraw.current.clear();
        }
    }, 50), []);

    // Batched redraw functions
    const queueRowRedraw = useCallback((rowNodes: any[]) => {
        rowNodes.forEach(node => pendingRowsToRedraw.current.add(node));
        flushRedrawQueue();
    }, [flushRedrawQueue]);
 
    const queueFullRedraw = useCallback(() => {
        hasPendingFullRedraw.current = true;
        flushRedrawQueue();
    }, [flushRedrawQueue]);

    function handleApplyClick() {
        // the first entry in core facts contains every possible fact, all other entries are just subsets
        const allFacts = factConfig.core.find(v => v.value === "All Facts").factLists;
        const facts = Object.entries(selections).filter(([, value]) => value).reduce<DodFactSet[]>((facts, [value]) => {
            if (!value) return facts;
            const [id, type] = value.split(':');
            // attempt to find the fact in the current selections
            let fact = facts.find(fact => fact.id === Number(id));
            if (!fact) {
                // if it doesn't exist create it by cloning the value from the filters
                fact = cloneDeep(allFacts.find(fact => fact.id === Number(id)));
                facts.push(fact!);
            }
            fact![flagToFactMapping[type]] = true;
            return facts;
        }, []);
        onApply?.({
            name,
            value: facts,
        });
    }

    const handleCellClicked = (event: CellClickedEvent<DodFactSet>) => {
        const { rowIndex, colDef, event: browserEvent } = event;
        const flag = (colDef as ColDef).cellRendererParams?.flag;
        const mouseEvent = browserEvent as MouseEvent;

        if (flag && mouseEvent && rowIndex !== null && (mouseEvent.target as HTMLElement).matches('input[type="checkbox"]')) {
            handleCheckboxClick(mouseEvent, rowIndex, flag);
        }
    };

    return (
        <div className={classNames(baseClassName, className)} {...props}>
            <div className={`${baseClassName}__options`}>
                <ByzzerTable ref={gridRef}
                    gridOptions={checkboxGridOptions}
                    context={selections}
                    key={`${filterType?.type}`}
                    rowData={matchingFacts}
                    suppressRowHoverHighlight
                    suppressCellFocus
                    suppressMovableColumns
                    columnDefs={filterType.data.type === 'core' ? coreFactsColumnDefs : shareFactsColumnDefs}
                    tableArea={filterType.data.type === 'core' ? 'dodCoreFactValuePicker' : 'dodShareFactValuePicker'}
                    defaultColDef={defaultColDef}
                    enableSaveLayout={true}
                    enableSaveSort={false} // BYZ-15636 better practice to pass value for enableSaveSort along with enableSaveLayout prop
                    factTriggerRef={factTriggerRef}
                    uncheckedColumns={extendedDataYears ? [] : ['fourYearAgoCheckbox', 'threeYearAgoCheckbox']}
                    onCellClicked={handleCellClicked}
                />
            </div>

            <div className={`${baseClassName}__actions`}>
                {actions?.map((config, index) => (
                    <ByzzerButton
                        key={index}
                        type={config.type}
                        className={config.className}
                        iconType={config.icon}
                        tip={config.tip}
                        tipDelay={[500, 0]}
                        label={config.label}
                        onClick={() => config.action()}
                    />
                ))}
                <div className={`${baseClassName}__actions-divider`} />
                <ByzzerButton type={'negative'} onClick={handleClearClick}>
                    Clear
                </ByzzerButton>
                <ByzzerButton onClick={handleApplyClick}>Apply</ByzzerButton>
            </div>
        </div>
    );
};

export default DodFactValuePickerTable;
